import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// ─── Toggle ───────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-1 ${
      checked ? 'bg-brand-earth' : 'bg-border'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-ios-light ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// ─── Section Card ─────────────────────────────────────────────────────────────
const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, children }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center space-x-3 px-5 py-4 border-b border-border/40">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
        {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

// ─── Row Item ─────────────────────────────────────────────────────────────────
const RowItem = ({ icon, label, description = null, children }) => (
  <div className="flex items-center justify-between py-3.5 border-b border-border/30 last:border-0">
    <div className="flex items-start space-x-3 flex-1 min-w-0 mr-4">
      {icon && (
        <div className="w-7 h-7 rounded-lg bg-surface flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon name={icon} size={14} className="text-text-secondary" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-ios-callout font-body-medium text-text-primary">{label}</p>
        {description && <p className="text-ios-caption1 text-text-secondary mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'account', label: 'Compte', icon: 'UserCircle' },
  { id: 'language', label: 'Langue', icon: 'Globe' },
  { id: 'notifications', label: 'Notifications', icon: 'Bell' },
  { id: 'privacy', label: 'Confidentialité', icon: 'Shield' },
];

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷', region: 'France / Côte d\'Ivoire' },
  { code: 'en', label: 'English', flag: '🇬🇧', region: 'United Kingdom' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', region: 'Arabe' },
  { code: 'pt', label: 'Português', flag: '🇧🇷', region: 'Brésil / Portugal' },
  { code: 'es', label: 'Español', flag: '🇪🇸', region: 'Espagne / Amérique Latine' },
];

// ─── Account Tab ──────────────────────────────────────────────────────────────
const AccountTab = ({ user, profile, onSave, saving }) => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    bio: '',
    role: 'investor',
  });
  const [editField, setEditField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [localSaving, setLocalSaving] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile?.full_name || user?.user_metadata?.full_name || '',
        email: user?.email || '',
        phone: profile?.phone || user?.user_metadata?.phone || '',
        bio: profile?.bio || '',
        role: profile?.role || 'investor',
      });
    } else if (user) {
      setForm(f => ({
        ...f,
        email: user?.email || '',
        full_name: user?.user_metadata?.full_name || '',
        phone: user?.user_metadata?.phone || '',
      }));
    }
  }, [profile, user]);

  const startEdit = (field) => {
    setEditField(field);
    setFieldValue(form?.[field] || '');
  };

  const cancelEdit = () => {
    setEditField(null);
    setFieldValue('');
  };

  const saveField = async (field) => {
    setLocalSaving(true);
    try {
      const updates = { [field]: fieldValue };
      if (field === 'email') {
        const { error } = await supabase?.auth?.updateUser({ email: fieldValue });
        if (error) throw error;
        setSuccess('Un email de confirmation a été envoyé à votre nouvelle adresse.');
      } else {
        await onSave(updates);
        setSuccess('Modification enregistrée.');
      }
      setForm(f => ({ ...f, [field]: fieldValue }));
      setEditField(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLocalSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const roleOptions = [
    { value: 'investor', label: 'Investisseur', icon: 'TrendingUp', desc: 'Accès aux outils d\'investissement et watchlist' },
    { value: 'professional', label: 'Professionnel', icon: 'Briefcase', desc: 'Accès au dashboard pro et gestion des réservations' },
    { value: 'buyer', label: 'Acheteur', icon: 'Home', desc: 'Recherche et achat de propriétés' },
  ];

  const fields = [
    { key: 'full_name', label: 'Nom complet', icon: 'User', type: 'text', placeholder: 'Votre nom complet' },
    { key: 'email', label: 'Adresse email', icon: 'Mail', type: 'email', placeholder: 'votre@email.com' },
    { key: 'phone', label: 'Téléphone', icon: 'Phone', type: 'tel', placeholder: '+225 XX XX XX XX' },
    { key: 'bio', label: 'Biographie', icon: 'FileText', type: 'textarea', placeholder: 'Décrivez-vous en quelques mots...' },
  ];

  return (
    <div className="space-y-5">
      {success && (
        <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-green-700 text-ios-callout">
          <Icon name="CheckCircle" size={16} />
          <span>{success}</span>
        </div>
      )}
      {/* Avatar */}
      <SectionCard icon="UserCircle" title="Photo de profil" subtitle="Votre avatar visible par les autres utilisateurs">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-gold to-brand-earth flex items-center justify-center shadow-ios-light flex-shrink-0">
            <span className="text-white text-2xl font-display font-display-semibold">
              {form?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <p className="text-ios-callout font-body-semibold text-text-primary">
              {form?.full_name || 'Utilisateur'}
            </p>
            <p className="text-ios-caption1 text-text-secondary mt-0.5">{form?.email}</p>
            <p className="text-ios-caption2 text-brand-gold mt-1 font-body-medium">
              Membre depuis {new Date(user?.created_at || Date.now())?.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </SectionCard>
      {/* Editable Fields */}
      <SectionCard icon="Edit3" title="Informations personnelles" subtitle="Gérez vos coordonnées et informations de contact">
        <div className="space-y-0">
          {fields?.map(field => (
            <div key={field?.key}>
              {editField === field?.key ? (
                <div className="py-3.5 border-b border-border/30 last:border-0">
                  <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-2">{field?.label}</label>
                  {field?.type === 'textarea' ? (
                    <textarea
                      rows={3}
                      value={fieldValue}
                      onChange={e => setFieldValue(e?.target?.value)}
                      placeholder={field?.placeholder}
                      className="w-full px-3 py-2.5 border border-border rounded-xl text-ios-callout focus:ring-2 focus:ring-brand-gold focus:border-transparent resize-none"
                    />
                  ) : (
                    <input
                      type={field?.type}
                      value={fieldValue}
                      onChange={e => setFieldValue(e?.target?.value)}
                      placeholder={field?.placeholder}
                      className="w-full px-3 py-2.5 border border-border rounded-xl text-ios-callout focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                    />
                  )}
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => saveField(field?.key)}
                      disabled={localSaving}
                      className="flex items-center space-x-1.5 px-4 py-2 bg-brand-earth text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-brand-earth/90 transition-colors disabled:opacity-50"
                    >
                      <Icon name="Check" size={13} />
                      <span>{localSaving ? 'Enregistrement...' : 'Enregistrer'}</span>
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center space-x-1.5 px-4 py-2 bg-surface text-text-secondary rounded-xl text-ios-caption1 font-body-medium hover:bg-border/40 transition-colors"
                    >
                      <Icon name="X" size={13} />
                      <span>Annuler</span>
                    </button>
                  </div>
                </div>
              ) : (
                <RowItem icon={field?.icon} label={field?.label} description={form?.[field?.key] || null}>
                  <div className="flex items-center space-x-2">
                    <span className="text-ios-callout text-text-secondary max-w-[160px] truncate">
                      {form?.[field?.key] || <span className="text-border italic">Non renseigné</span>}
                    </span>
                    <button
                      onClick={() => startEdit(field?.key)}
                      className="p-1.5 rounded-lg hover:bg-surface transition-colors text-text-secondary hover:text-brand-earth"
                    >
                      <Icon name="Pencil" size={14} />
                    </button>
                  </div>
                </RowItem>
              )}
            </div>
          ))}
        </div>
      </SectionCard>
      {/* Role */}
      <SectionCard icon="Tag" title="Type de compte" subtitle="Votre profil détermine les fonctionnalités disponibles">
        <div className="space-y-2">
          {roleOptions?.map(opt => (
            <button
              key={opt?.value}
              onClick={() => onSave({ role: opt?.value })}
              className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl border transition-all duration-150 text-left ${
                form?.role === opt?.value
                  ? 'border-brand-gold bg-brand-gold/5 shadow-ios-light'
                  : 'border-border/60 bg-white hover:border-brand-gold/40 hover:bg-surface/50'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                form?.role === opt?.value ? 'bg-brand-earth/15' : 'bg-surface'
              }`}>
                <Icon name={opt?.icon} size={17} className={form?.role === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-ios-callout font-body-medium ${form?.role === opt?.value ? 'text-brand-earth' : 'text-text-primary'}`}>
                  {opt?.label}
                </p>
                <p className="text-ios-caption1 text-text-secondary mt-0.5">{opt?.desc}</p>
              </div>
              {form?.role === opt?.value && (
                <Icon name="CheckCircle" size={18} className="text-brand-gold flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </SectionCard>
      {/* Password */}
      <SectionCard icon="Lock" iconBg="bg-amber-50" iconColor="text-amber-600" title="Sécurité du compte" subtitle="Gérez votre mot de passe et la sécurité">
        <div className="space-y-3">
          <Link
            to="/auth"
            className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-border/60 hover:border-brand-gold/40 hover:bg-surface/50 transition-all duration-150 group"
          >
            <div className="flex items-center space-x-3">
              <Icon name="KeyRound" size={16} className="text-text-secondary group-hover:text-brand-earth" />
              <span className="text-ios-callout font-body-medium text-text-primary">Changer le mot de passe</span>
            </div>
            <Icon name="ChevronRight" size={16} className="text-text-secondary" />
          </Link>
          <Link
            to="/account-dashboard"
            className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-border/60 hover:border-brand-gold/40 hover:bg-surface/50 transition-all duration-150 group"
          >
            <div className="flex items-center space-x-3">
              <Icon name="ShieldCheck" size={16} className="text-text-secondary group-hover:text-brand-earth" />
              <span className="text-ios-callout font-body-medium text-text-primary">Authentification à deux facteurs</span>
            </div>
            <Icon name="ChevronRight" size={16} className="text-text-secondary" />
          </Link>
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Language Tab ─────────────────────────────────────────────────────────────
const LanguageTab = ({ prefs, onUpdate }) => {
  const [selected, setSelected] = useState(prefs?.language || 'fr');
  const [dateFormat, setDateFormat] = useState(prefs?.date_format || 'dd/mm/yyyy');
  const [currency, setCurrency] = useState(prefs?.currency || 'XOF');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onUpdate({ language: selected, date_format: dateFormat, currency });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-5">
      {saved && (
        <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-green-700 text-ios-callout">
          <Icon name="CheckCircle" size={16} />
          <span>Préférences de langue enregistrées.</span>
        </div>
      )}
      <SectionCard icon="Globe" title="Langue d'affichage" subtitle="Choisissez la langue de l'interface">
        <div className="space-y-2">
          {LANGUAGES?.map(lang => (
            <button
              key={lang?.code}
              onClick={() => setSelected(lang?.code)}
              className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl border transition-all duration-150 text-left ${
                selected === lang?.code
                  ? 'border-brand-gold bg-brand-gold/5 shadow-ios-light'
                  : 'border-border/60 bg-white hover:border-brand-gold/40 hover:bg-surface/50'
              }`}
            >
              <span className="text-2xl flex-shrink-0">{lang?.flag}</span>
              <div className="flex-1">
                <p className={`text-ios-callout font-body-medium ${selected === lang?.code ? 'text-brand-earth' : 'text-text-primary'}`}>
                  {lang?.label}
                </p>
                <p className="text-ios-caption1 text-text-secondary">{lang?.region}</p>
              </div>
              {selected === lang?.code && <Icon name="CheckCircle" size={18} className="text-brand-gold flex-shrink-0" />}
            </button>
          ))}
        </div>
      </SectionCard>
      <SectionCard icon="Calendar" title="Format de date" subtitle="Comment les dates sont affichées">
        <div className="flex flex-wrap gap-2">
          {['dd/mm/yyyy', 'mm/dd/yyyy', 'yyyy-mm-dd']?.map(fmt => (
            <button
              key={fmt}
              onClick={() => setDateFormat(fmt)}
              className={`px-4 py-2 rounded-xl border text-ios-callout font-body-medium transition-all duration-150 ${
                dateFormat === fmt
                  ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
                  : 'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50 hover:text-brand-earth'
              }`}
            >
              {fmt}
            </button>
          ))}
        </div>
      </SectionCard>
      <SectionCard icon="DollarSign" title="Devise" subtitle="Devise utilisée pour les prix">
        <div className="flex flex-wrap gap-2">
          {[
            { code: 'XOF', label: 'XOF — Franc CFA' },
            { code: 'EUR', label: 'EUR — Euro' },
            { code: 'USD', label: 'USD — Dollar US' },
          ]?.map(c => (
            <button
              key={c?.code}
              onClick={() => setCurrency(c?.code)}
              className={`px-4 py-2 rounded-xl border text-ios-callout font-body-medium transition-all duration-150 ${
                currency === c?.code
                  ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
                  : 'bg-white text-text-secondary border-border/60 hover:border-brand-gold/50 hover:text-brand-earth'
              }`}
            >
              {c?.label}
            </button>
          ))}
        </div>
      </SectionCard>
      <button
        onClick={handleSave}
        className="w-full flex items-center justify-center space-x-2 px-6 py-3.5 bg-brand-earth text-white rounded-2xl font-body-semibold text-ios-callout hover:bg-brand-earth/90 transition-colors shadow-ios-light"
      >
        <Icon name="Save" size={16} />
        <span>Enregistrer les préférences</span>
      </button>
    </div>
  );
};

// ─── Notifications Tab ────────────────────────────────────────────────────────
const NotificationsTab = ({ prefs, onUpdate }) => {
  const [settings, setSettings] = useState({
    email_new_property: true,
    email_price_drop: true,
    email_appointment_reminder: true,
    email_messages: true,
    email_weekly_digest: false,
    email_promotions: false,
    sms_appointment_reminder: true,
    sms_price_drop: false,
    sms_messages: false,
    push_new_property: true,
    push_messages: true,
    push_appointments: true,
    frequency: 'immediate',
    ...prefs,
  });
  const [saved, setSaved] = useState(false);

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = () => {
    onUpdate(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const emailItems = [
    { key: 'email_new_property', label: 'Nouvelles propriétés', desc: 'Alertes pour les nouvelles annonces correspondant à vos critères', icon: 'Home' },
    { key: 'email_price_drop', label: 'Baisses de prix', desc: 'Notification quand le prix d\'une propriété en watchlist baisse', icon: 'TrendingDown' },
    { key: 'email_appointment_reminder', label: 'Rappels de rendez-vous', desc: '24h avant chaque rendez-vous planifié', icon: 'CalendarCheck' },
    { key: 'email_messages', label: 'Nouveaux messages', desc: 'Quand vous recevez un message dans la messagerie', icon: 'MessageSquare' },
    { key: 'email_weekly_digest', label: 'Digest hebdomadaire', desc: 'Résumé de votre activité chaque lundi matin', icon: 'MailOpen' },
    { key: 'email_promotions', label: 'Offres et promotions', desc: 'Actualités et offres spéciales de Kapital States', icon: 'Tag' },
  ];

  const smsItems = [
    { key: 'sms_appointment_reminder', label: 'Rappels de rendez-vous', desc: 'SMS de rappel 2h avant le rendez-vous', icon: 'CalendarCheck' },
    { key: 'sms_price_drop', label: 'Baisses de prix urgentes', desc: 'SMS pour les baisses de prix importantes (>10%)', icon: 'TrendingDown' },
    { key: 'sms_messages', label: 'Nouveaux messages', desc: 'SMS quand vous recevez un message urgent', icon: 'MessageSquare' },
  ];

  const pushItems = [
    { key: 'push_new_property', label: 'Nouvelles propriétés', desc: 'Notification push pour les nouvelles annonces', icon: 'Home' },
    { key: 'push_messages', label: 'Messages', desc: 'Notification push pour les nouveaux messages', icon: 'MessageSquare' },
    { key: 'push_appointments', label: 'Rendez-vous', desc: 'Rappels push pour vos rendez-vous', icon: 'CalendarCheck' },
  ];

  const freqOptions = [
    { value: 'immediate', label: 'Immédiat', icon: 'Zap', desc: 'Dès que l\'événement se produit' },
    { value: 'daily', label: 'Quotidien', icon: 'Sun', desc: 'Résumé une fois par jour' },
    { value: 'weekly', label: 'Hebdomadaire', icon: 'CalendarDays', desc: 'Résumé une fois par semaine' },
  ];

  return (
    <div className="space-y-5">
      {saved && (
        <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-green-700 text-ios-callout">
          <Icon name="CheckCircle" size={16} />
          <span>Préférences de notifications enregistrées.</span>
        </div>
      )}
      {/* Frequency */}
      <SectionCard icon="Clock" title="Fréquence des notifications" subtitle="À quelle fréquence souhaitez-vous être notifié ?">
        <div className="space-y-2">
          {freqOptions?.map(opt => (
            <button
              key={opt?.value}
              onClick={() => update('frequency', opt?.value)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl border transition-all duration-150 text-left ${
                settings?.frequency === opt?.value
                  ? 'border-brand-gold bg-brand-gold/5 shadow-ios-light'
                  : 'border-border/60 bg-white hover:border-brand-gold/40 hover:bg-surface/50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                settings?.frequency === opt?.value ? 'bg-brand-earth/15' : 'bg-surface'
              }`}>
                <Icon name={opt?.icon} size={15} className={settings?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
              </div>
              <div className="flex-1">
                <p className={`text-ios-callout font-body-medium ${settings?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-primary'}`}>
                  {opt?.label}
                </p>
                <p className="text-ios-caption1 text-text-secondary">{opt?.desc}</p>
              </div>
              {settings?.frequency === opt?.value && <Icon name="CheckCircle" size={16} className="text-brand-gold flex-shrink-0" />}
            </button>
          ))}
        </div>
      </SectionCard>
      {/* Email */}
      <SectionCard icon="Mail" title="Notifications par email" subtitle="Choisissez les emails que vous souhaitez recevoir">
        <div className="space-y-0">
          {emailItems?.map(item => (
            <RowItem key={item?.key} icon={item?.icon} label={item?.label} description={item?.desc}>
              <Toggle checked={settings?.[item?.key]} onChange={v => update(item?.key, v)} />
            </RowItem>
          ))}
        </div>
      </SectionCard>
      {/* SMS */}
      <SectionCard icon="Smartphone" iconBg="bg-blue-50" iconColor="text-blue-600" title="Notifications par SMS" subtitle="Recevez des alertes importantes par SMS">
        <div className="space-y-0">
          {smsItems?.map(item => (
            <RowItem key={item?.key} icon={item?.icon} label={item?.label} description={item?.desc}>
              <Toggle checked={settings?.[item?.key]} onChange={v => update(item?.key, v)} />
            </RowItem>
          ))}
        </div>
      </SectionCard>
      {/* Push */}
      <SectionCard icon="Bell" iconBg="bg-purple-50" iconColor="text-purple-600" title="Notifications push" subtitle="Notifications dans l'application">
        <div className="space-y-0">
          {pushItems?.map(item => (
            <RowItem key={item?.key} icon={item?.icon} label={item?.label} description={item?.desc}>
              <Toggle checked={settings?.[item?.key]} onChange={v => update(item?.key, v)} />
            </RowItem>
          ))}
        </div>
      </SectionCard>
      <button
        onClick={handleSave}
        className="w-full flex items-center justify-center space-x-2 px-6 py-3.5 bg-brand-earth text-white rounded-2xl font-body-semibold text-ios-callout hover:bg-brand-earth/90 transition-colors shadow-ios-light"
      >
        <Icon name="Save" size={16} />
        <span>Enregistrer les préférences</span>
      </button>
    </div>
  );
};

// ─── Privacy Tab ──────────────────────────────────────────────────────────────
const PrivacyTab = ({ prefs, onUpdate }) => {
  const [settings, setSettings] = useState({
    profile_visible: true,
    show_activity: false,
    show_watchlist: false,
    allow_contact: true,
    data_analytics: true,
    data_personalization: true,
    data_third_party: false,
    two_factor_enabled: false,
    ...prefs,
  });
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = () => {
    onUpdate(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const visibilityItems = [
    { key: 'profile_visible', label: 'Profil public', desc: 'Votre profil est visible par les autres utilisateurs', icon: 'Eye' },
    { key: 'show_activity', label: 'Activité visible', desc: 'Les autres peuvent voir votre activité récente', icon: 'Activity' },
    { key: 'show_watchlist', label: 'Watchlist visible', desc: 'Votre liste de propriétés sauvegardées est publique', icon: 'Bookmark' },
    { key: 'allow_contact', label: 'Autoriser les contacts', desc: 'Les professionnels peuvent vous contacter directement', icon: 'MessageSquare' },
  ];

  const dataItems = [
    { key: 'data_analytics', label: 'Analyses d\'utilisation', desc: 'Nous aider à améliorer l\'application avec des données anonymes', icon: 'BarChart2' },
    { key: 'data_personalization', label: 'Personnalisation', desc: 'Utiliser vos données pour personnaliser votre expérience', icon: 'Sliders' },
    { key: 'data_third_party', label: 'Partage tiers', desc: 'Partager vos données avec nos partenaires de confiance', icon: 'Share2' },
  ];

  return (
    <div className="space-y-5">
      {saved && (
        <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-green-700 text-ios-callout">
          <Icon name="CheckCircle" size={16} />
          <span>Paramètres de confidentialité enregistrés.</span>
        </div>
      )}
      {/* Visibility */}
      <SectionCard icon="Eye" title="Visibilité du profil" subtitle="Contrôlez ce que les autres utilisateurs peuvent voir">
        <div className="space-y-0">
          {visibilityItems?.map(item => (
            <RowItem key={item?.key} icon={item?.icon} label={item?.label} description={item?.desc}>
              <Toggle checked={settings?.[item?.key]} onChange={v => update(item?.key, v)} />
            </RowItem>
          ))}
        </div>
      </SectionCard>
      {/* Data */}
      <SectionCard icon="Database" iconBg="bg-blue-50" iconColor="text-blue-600" title="Utilisation des données" subtitle="Contrôlez comment vos données sont utilisées">
        <div className="space-y-0">
          {dataItems?.map(item => (
            <RowItem key={item?.key} icon={item?.icon} label={item?.label} description={item?.desc}>
              <Toggle checked={settings?.[item?.key]} onChange={v => update(item?.key, v)} />
            </RowItem>
          ))}
        </div>
      </SectionCard>
      {/* Legal links */}
      <SectionCard icon="FileText" iconBg="bg-amber-50" iconColor="text-amber-600" title="Documents légaux" subtitle="Consultez nos politiques de confidentialité">
        <div className="space-y-2">
          {[
            { label: 'Politique de confidentialité', path: '/legal-compliance-footer', icon: 'Shield' },
            { label: 'Conditions d\'utilisation', path: '/legal-compliance-footer', icon: 'FileText' },
            { label: 'Politique des cookies', path: '/legal-compliance-footer', icon: 'Cookie' },
          ]?.map(doc => (
            <Link
              key={doc?.label}
              to={doc?.path}
              className="flex items-center justify-between px-4 py-3 rounded-xl border border-border/60 hover:border-brand-gold/40 hover:bg-surface/50 transition-all duration-150 group"
            >
              <div className="flex items-center space-x-3">
                <Icon name={doc?.icon} size={15} className="text-text-secondary group-hover:text-brand-earth" />
                <span className="text-ios-callout font-body-medium text-text-primary">{doc?.label}</span>
              </div>
              <Icon name="ExternalLink" size={14} className="text-text-secondary" />
            </Link>
          ))}
        </div>
      </SectionCard>
      {/* Danger zone */}
      <SectionCard icon="AlertTriangle" iconBg="bg-red-50" iconColor="text-red-500" title="Zone de danger" subtitle="Actions irréversibles sur votre compte">
        <div className="space-y-3">
          {!deleteConfirm ? (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="flex items-center space-x-2 px-4 py-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-all duration-150 text-ios-callout font-body-medium w-full"
            >
              <Icon name="Trash2" size={16} />
              <span>Supprimer mon compte</span>
            </button>
          ) : (
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-ios-callout font-body-semibold text-red-700 mb-2">Êtes-vous sûr ?</p>
              <p className="text-ios-caption1 text-red-600 mb-4">Cette action est irréversible. Toutes vos données seront supprimées définitivement.</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 px-4 py-2.5 bg-white border border-border rounded-xl text-ios-callout font-body-medium text-text-primary hover:bg-surface transition-colors"
                >
                  Annuler
                </button>
                <button
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-red-700 transition-colors"
                >
                  Confirmer la suppression
                </button>
              </div>
            </div>
          )}
        </div>
      </SectionCard>
      <button
        onClick={handleSave}
        className="w-full flex items-center justify-center space-x-2 px-6 py-3.5 bg-brand-earth text-white rounded-2xl font-body-semibold text-ios-callout hover:bg-brand-earth/90 transition-colors shadow-ios-light"
      >
        <Icon name="Save" size={16} />
        <span>Enregistrer les paramètres</span>
      </button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const UserProfileManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [profile, setProfile] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({});
  const [privacyPrefs, setPrivacyPrefs] = useState({});
  const [langPrefs, setLangPrefs] = useState({});
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: prof } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.maybeSingle();
      if (prof) {
        setProfile(prof);
        setNotifPrefs(prof?.notification_preferences || {});
        setPrivacyPrefs(prof?.privacy_settings || {});
        setLangPrefs({
          language: prof?.language || 'fr',
          date_format: prof?.date_format || 'dd/mm/yyyy',
          currency: prof?.currency || 'XOF',
        });
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const saveProfile = async (updates) => {
    if (!user?.id) return;
    try {
      const fallbackName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
      const { error } = await supabase?.from('user_profiles')?.upsert({ id: user?.id, email: user?.email, full_name: fallbackName, ...updates, updated_at: new Date()?.toISOString() }, { onConflict: 'id' });
      if (error) throw error;
      setProfile(p => ({ ...p, ...updates }));
    } catch (err) {
      console.error('Error saving profile:', err);
    }
  };

  const saveNotifPrefs = async (prefs) => {
    await saveProfile({ notification_preferences: prefs });
    setNotifPrefs(prefs);
  };

  const savePrivacyPrefs = async (prefs) => {
    await saveProfile({ privacy_settings: prefs });
    setPrivacyPrefs(prefs);
  };

  const saveLangPrefs = async (prefs) => {
    await saveProfile(prefs);
    setLangPrefs(prefs);
  };

  return (
    <>
      <Helmet>
        <title>Mon Espace — Gestion du profil | Kapital States</title>
        <meta name="description" content="Gérez votre profil, vos préférences de notifications et vos paramètres de confidentialité." />
      </Helmet>
      <div className="min-h-screen bg-[#F5F5F7]">
        <Header />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={12} />
              <span className="text-text-primary font-body-medium">Mon Espace</span>
              <Icon name="ChevronRight" size={12} />
              <span className="text-brand-earth font-body-medium">Gestion du profil</span>
            </div>
            <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth tracking-tight">
              Gestion du profil
            </h1>
            <p className="text-ios-callout text-text-secondary mt-1.5">
              Gérez vos informations personnelles, préférences et paramètres de confidentialité.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-white rounded-2xl p-1.5 shadow-ios-light border border-border/50 mb-6 overflow-x-auto">
            {TABS?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-ios-callout font-body-medium transition-all duration-200 whitespace-nowrap flex-1 justify-center ${
                  activeTab === tab?.id
                    ? 'bg-brand-earth text-white shadow-ios-light'
                    : 'text-text-secondary hover:text-brand-earth hover:bg-surface/70'
                }`}
              >
                <Icon name={tab?.icon} size={15} strokeWidth={2} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-10 h-10 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                <p className="text-ios-callout text-text-secondary">Chargement du profil...</p>
              </div>
            </div>
          ) : !user ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mb-4">
                <Icon name="UserCircle" size={32} className="text-brand-earth" />
              </div>
              <h3 className="text-ios-title3 font-display font-display-semibold text-text-primary mb-2">
                Connexion requise
              </h3>
              <p className="text-ios-callout text-text-secondary mb-6 max-w-xs">
                Connectez-vous pour accéder à la gestion de votre profil.
              </p>
              <Link
                to="/auth"
                className="flex items-center space-x-2 px-6 py-3 bg-brand-earth text-white rounded-2xl font-body-semibold text-ios-callout hover:bg-brand-earth/90 transition-colors shadow-ios-light"
              >
                <Icon name="LogIn" size={16} />
                <span>Se connecter</span>
              </Link>
            </div>
          ) : (
            <div>
              {activeTab === 'account' && (
                <AccountTab user={user} profile={profile} onSave={saveProfile} saving={false} />
              )}
              {activeTab === 'language' && (
                <LanguageTab prefs={langPrefs} onUpdate={saveLangPrefs} />
              )}
              {activeTab === 'notifications' && (
                <NotificationsTab prefs={notifPrefs} onUpdate={saveNotifPrefs} />
              )}
              {activeTab === 'privacy' && (
                <PrivacyTab prefs={privacyPrefs} onUpdate={savePrivacyPrefs} />
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default UserProfileManagement;
